import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Send,
  MessageSquare,
  ChevronDown,
  User,
  Bot,
  Clock,
  Phone,
  MoreVertical,
  Search,
  Plus,
  Loader2,
  Trash2,
  AlertTriangle,
  X,
  CheckCircle,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

// Telegram brand color
const TELEGRAM_COLOR = "#229ED9";

// Format timestamp helper
const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
};

// Chat List Item Component
const ChatListItem = ({ chat, isSelected, onClick }) => {
  // Extract last message from chat_history
  const lastMessage = chat.chat_history && chat.chat_history.length > 0 
    ? chat.chat_history[chat.chat_history.length - 1] 
    : null;
  
  const lastMessageText = lastMessage?.content || "No messages yet";
  const lastMessageTime = chat.timestamps?.updated_at || lastMessage?.timestamp;

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 flex items-start gap-3 transition-all text-left ${
        isSelected
          ? "bg-[#229ED9]/10 border-l-2 border-[#229ED9]"
          : "hover:bg-white/5 border-l-2 border-transparent"
      }`}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
        <User className="w-5 h-5 text-[#9CA3AF]" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="font-body text-sm text-[#F3F4F6] font-medium">
            {chat.lead_name || chat.lead_contact || "Unknown Contact"}
          </span>
          <span className="text-xs text-[#9CA3AF]">
            {formatDate(lastMessageTime)}
          </span>
        </div>
        {chat.lead_name && chat.lead_contact && (
          <p className="text-xs text-[#9CA3AF] mb-1">{chat.lead_contact}</p>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#229ED9] capitalize">{chat.platform}</span>
          {chat.ai_active && (
            <span className="text-xs text-green-400">• AI Active</span>
          )}
        </div>
        <p className="text-sm text-[#9CA3AF] truncate mt-1">{lastMessageText}</p>
      </div>
    </button>
  );
};

// Message Bubble Component
const MessageBubble = ({ message }) => {
  const isUser = message.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div
        className={`max-w-[75%] ${
          isUser
            ? "bg-[#229ED9] text-white"
            : "bg-white/10 text-[#F3F4F6]"
        } rounded-2xl px-4 py-3 ${
          isUser ? "rounded-br-md" : "rounded-bl-md"
        }`}
      >
        <p className="text-sm font-body leading-relaxed">{message.content}</p>
        <p
          className={`text-xs mt-2 ${
            isUser ? "text-white/60" : "text-[#9CA3AF]"
          }`}
        >
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
};

// Telegram icon SVG component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

// Main Chat Telegram Page Component
const ChatTelegramApp = () => {
  const [searchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(null); // chat_id of open menu
  const [deleteConfirm, setDeleteConfirm] = useState(null); // chat_id to confirm deletion
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isTogglingAI, setIsTogglingAI] = useState(false);
  const [notification, setNotification] = useState(null); // {type: 'success' | 'error', message: string}
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Fetch chat history for a specific chat
  const fetchChatHistory = useCallback(async (chatId) => {
    // Skip if we already have messages for this chat
    if (chatMessages[chatId] && chatMessages[chatId].length > 0) {
      return;
    }

    const token = getClientAuthToken();
    if (!token) {
      return;
    }

    setIsLoadingHistory(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/agent/v1/get-test-chat-history`,
        {
          token: token,
          chat_id: chatId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "ok") {
        setChatMessages((prev) => ({
          ...prev,
          [chatId]: response.data.chat_history,
        }));
      }
    } catch (err) {
      console.error("Error fetching chat history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [chatMessages]);

  // Fetch Telegram chats from API
  useEffect(() => {
    const fetchTelegramChats = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const token = getClientAuthToken();
    if (!token) {
          setError("Not authenticated");
          setIsLoading(false);
          return;
        }

        const response = await axios.get(`${BACKEND_URL}/api/agent/v1/get_telegram_chats`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.status === "ok") {
          setChats(response.data.chats);
          // If ?id= query param is present and matches a chat, select it; otherwise select first
          const queryChatId = searchParams.get("id");
          const loadedChats = response.data.chats;
          if (queryChatId && loadedChats.some((c) => c.id === queryChatId)) {
            setSelectedChatId(queryChatId);
          } else if (loadedChats.length > 0) {
            setSelectedChatId(loadedChats[0].id);
          }
        }
      } catch (err) {
        console.error("Error fetching Telegram chats:", err);
        setError("Failed to load chats");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTelegramChats();
  }, []);

  // Fetch chat history when selected chat changes
  useEffect(() => {
    if (selectedChatId) {
      fetchChatHistory(selectedChatId);
    }
  }, [selectedChatId, fetchChatHistory]);

  // Get current chat
  const selectedChat = chats.find((c) => c.id === selectedChatId);
  const currentMessages = chatMessages[selectedChatId] || [];

  // Filter chats based on search
  const filteredChats = chats.filter((chat) => {
    const lastMessage = chat.chat_history?.[0]?.content || "";
    return (
      chat.lead_contact?.includes(searchQuery) ||
      lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentMessages]);

  // Handle selecting a chat
  const handleSelectChat = (chatId) => {
    setSelectedChatId(chatId);
    setIsMenuOpen(null); // Close any open menu
  };

  // Show delete confirmation
  const showDeleteConfirm = (chatId) => {
    setDeleteConfirm(chatId);
    setIsMenuOpen(null);
  };

  // Handle delete chat
  const handleDeleteChat = async (chatId) => {
    const token = getClientAuthToken();
    if (!token) {
      setError("Not authenticated");
      setDeleteConfirm(null);
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/agent/v1/delete-chat/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Remove chat from local state
      setChats((prev) => prev.filter((chat) => chat.id !== chatId));
      
      // Remove chat messages from local state
      setChatMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[chatId];
        return newMessages;
      });

      // If the deleted chat was selected, select another chat
      if (selectedChatId === chatId) {
        const remainingChats = chats.filter((chat) => chat.id !== chatId);
        if (remainingChats.length > 0) {
          setSelectedChatId(remainingChats[0].id);
        } else {
          setSelectedChatId(null);
        }
      }

      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting chat:", err);
      setError("Failed to delete chat");
      setDeleteConfirm(null);
    }
  };

  // Handle toggle AI active
  const handleToggleAI = async (chatId, newAiActive) => {
    const token = getClientAuthToken();
    if (!token) {
      setNotification({ type: 'error', message: 'Not authenticated' });
      return;
    }

    setIsTogglingAI(true);

    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/agent/v1/toggle-ai-active/${chatId}?ai_active=${newAiActive}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "ok") {
        // Update local state
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === chatId ? { ...chat, ai_active: newAiActive } : chat
          )
        );

        setNotification({
          type: 'success',
          message: response.data.message
        });
      }
    } catch (err) {
      console.error("Error toggling AI:", err);
      setNotification({
        type: 'error',
        message: 'Failed to update AI status'
      });
    } finally {
      setIsTogglingAI(false);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!message.trim() || !selectedChatId || isSending) return;

    const token = getClientAuthToken();
    if (!token) {
      setNotification({ type: 'error', message: 'Not authenticated' });
      return;
    }

    const messageText = message.trim();
    const newMessage = {
      role: "assistant",
      content: messageText,
      timestamp: new Date().toISOString(),
      manual: true,
    };

    // Add message to UI immediately (optimistic update)
    setChatMessages((prev) => ({
      ...prev,
      [selectedChatId]: [...(prev[selectedChatId] || []), newMessage],
    }));

    setMessage("");
    setIsSending(true);

    try {
      // Send message via backend API
      await axios.post(
        `${BACKEND_URL}/api/agent/v1/send_telegram_manual_message`,
        {
          chat_id: selectedChatId,
          message: messageText,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update the chat's last message in the list
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === selectedChatId
            ? {
                ...chat,
                chat_history: [newMessage],
                timestamps: {
                  ...chat.timestamps,
                  updated_at: new Date().toISOString(),
                },
              }
            : chat
        )
      );

      setNotification({ type: 'success', message: 'Message sent successfully' });
    } catch (err) {
      console.error("Error sending Telegram message:", err);
      
      // Remove the optimistic message on error
      setChatMessages((prev) => ({
        ...prev,
        [selectedChatId]: (prev[selectedChatId] || []).filter(
          (msg) => msg.timestamp !== newMessage.timestamp || msg.content !== messageText
        ),
      }));

      // Show error notification with API error message if available
      const errorDetail = err.response?.data?.detail || "Failed to send message. Please try again.";
      setNotification({ type: 'error', message: errorDetail });
    } finally {
      setIsSending(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115]">
      <Sidebar />

      {/* Main Content */}
      <div className="md:ml-64 min-h-screen">
        <div className="h-screen flex flex-col">
          {/* Header */}
          <header className="bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5 p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#229ED9]/10 flex items-center justify-center">
                  <TelegramIcon className="w-5 h-5 text-[#229ED9]" />
                </div>
                <div>
                  <h1 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6]">
                    Chat (Telegram)
                  </h1>
                  <p className="text-sm text-[#9CA3AF]">
                    View and manage Telegram conversations
                  </p>
                </div>
              </div>
            </div>
          </header>

          {/* Chat Interface Container */}
          <div className="flex-1 flex overflow-hidden">
            {/* Chat List Sidebar */}
            <div className="w-80 border-r border-white/5 flex flex-col bg-[#0F1115] hidden lg:flex">
              {/* Search */}
              <div className="p-4 border-b border-white/5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#229ED9]/50 focus:ring-1 focus:ring-[#229ED9]/50 text-sm"
                  />
                </div>
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="p-6 text-center">
                    <Loader2 className="w-6 h-6 text-[#229ED9] animate-spin mx-auto mb-2" />
                    <p className="text-[#9CA3AF] text-sm">Loading chats...</p>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                ) : filteredChats.length > 0 ? (
                  filteredChats.map((chat) => (
                    <ChatListItem
                      key={chat.id}
                      chat={chat}
                      isSelected={chat.id === selectedChatId}
                      onClick={() => handleSelectChat(chat.id)}
                    />
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <MessageSquare className="w-10 h-10 text-[#9CA3AF]/30 mx-auto mb-3" />
                    <p className="text-[#9CA3AF] text-sm">No Telegram chats yet</p>
                    <p className="text-[#9CA3AF]/60 text-xs mt-1">
                      Chats will appear here when customers message your Telegram bot
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col">
              {/* Mobile Chat Selector Dropdown */}
              <div className="lg:hidden border-b border-white/5 p-4">
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#9CA3AF]" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium">
                          {selectedChat?.lead_name || selectedChat?.lead_contact || "Select a chat"}
                        </p>
                        {selectedChat?.lead_name && selectedChat?.lead_contact && (
                          <p className="text-xs text-[#9CA3AF]">{selectedChat.lead_contact}</p>
                        )}
                        <p className="text-xs text-[#229ED9] capitalize">
                          {selectedChat?.platform}
                        </p>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-[#9CA3AF] transition-transform ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1d21] border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                      {chats.map((chat) => (
                        <button
                          key={chat.id}
                          onClick={() => {
                            handleSelectChat(chat.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full p-3 flex items-center gap-3 text-left hover:bg-white/5 ${
                            chat.id === selectedChatId ? "bg-[#229ED9]/10" : ""
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-[#9CA3AF]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#F3F4F6]">
                              {chat.lead_name || chat.lead_contact}
                            </p>
                            {chat.lead_name && chat.lead_contact && (
                              <p className="text-xs text-[#9CA3AF]">{chat.lead_contact}</p>
                            )}
                            <p className="text-xs text-[#9CA3AF] truncate">
                              {chat.chat_history?.[0]?.content || "No messages"}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Header */}
              {selectedChat && (
                <div className="hidden lg:flex items-center justify-between px-6 py-4 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#9CA3AF]" />
                    </div>
                    <div>
                      <h2 className="font-heading font-medium text-[#F3F4F6]">
                        {selectedChat.lead_name || selectedChat.lead_contact}
                      </h2>
                      <div className="flex items-center gap-2">
                        {selectedChat.lead_name && selectedChat.lead_contact && (
                          <>
                            <span className="text-xs text-[#9CA3AF]">
                              {selectedChat.lead_contact}
                            </span>
                            <span className="text-xs text-[#9CA3AF]">•</span>
                          </>
                        )}
                        <span className="text-xs text-[#229ED9] capitalize">
                          {selectedChat.platform}
                        </span>
                        <span className="text-xs text-[#9CA3AF]">•</span>
                        <span className="text-xs text-[#9CA3AF]">
                          Last active {formatDate(selectedChat.timestamps?.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* AI Toggle Switcher */}
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-medium ${selectedChat.ai_active ? 'text-[#229ED9]' : 'text-[#9CA3AF]'}`}>
                        {selectedChat.ai_active ? 'Digital Brain enabled' : 'Manual mode enabled'}
                      </span>
                      <button
                        onClick={() => handleToggleAI(selectedChat.id, !selectedChat.ai_active)}
                        disabled={isTogglingAI}
                        className={`relative w-11 h-6 rounded-full transition-all duration-200 ${
                          selectedChat.ai_active 
                            ? 'bg-[#229ED9]' 
                            : 'bg-white/20'
                        } ${isTogglingAI ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-200 ${
                            selectedChat.ai_active ? 'left-6' : 'left-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    <div className="relative">
                      <button 
                        onClick={() => setIsMenuOpen(isMenuOpen === selectedChat.id ? null : selectedChat.id)}
                        className="p-2 rounded-lg hover:bg-white/5 text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      {isMenuOpen === selectedChat.id && (
                        <div className="absolute right-0 top-full mt-1 bg-[#1a1d21] border border-white/10 rounded-lg shadow-xl z-50 min-w-[150px]">
                          <button
                            onClick={() => showDeleteConfirm(selectedChat.id)}
                            className="w-full px-4 py-2.5 flex items-center gap-2 text-left text-red-400 hover:bg-white/5 rounded-lg transition-all text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Chat
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
                {isLoadingHistory ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-[#229ED9] animate-spin mx-auto mb-3" />
                      <p className="text-[#9CA3AF] text-sm">Loading chat history...</p>
                    </div>
                  </div>
                ) : currentMessages.length > 0 ? (
                  <>
                    {currentMessages.map((msg, index) => (
                      <MessageBubble key={index} message={msg} />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                        <TelegramIcon className="w-8 h-8 text-[#9CA3AF]" />
                      </div>
                      <p className="text-[#9CA3AF] text-sm">
                        {chats.length === 0 
                          ? "No Telegram chats yet. Chats will appear when customers message your bot."
                          : "No messages yet. Start the conversation!"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="border-t border-white/5 p-4 md:p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-1 relative">
                    <textarea
                      ref={inputRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      rows={1}
                      disabled={isSending}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#229ED9]/50 focus:ring-1 focus:ring-[#229ED9]/50 resize-none font-body text-sm disabled:opacity-50"
                      style={{ minHeight: "48px", maxHeight: "120px" }}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isSending}
                    className={`p-3 rounded-xl transition-all ${
                      message.trim() && !isSending
                        ? "bg-[#229ED9] text-white hover:bg-[#229ED9]/90"
                        : "bg-white/10 text-[#9CA3AF] cursor-not-allowed"
                    }`}
                  >
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-[#9CA3AF]/60 mt-2 text-center">
                  Press Enter to send • Telegram Bot Integration
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-[#1a1d21] border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-[#F3F4F6]">Delete Chat</h3>
                <p className="text-sm text-[#9CA3AF]">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-[#9CA3AF] text-sm mb-6">
              Are you sure you want to delete this chat? All messages will be permanently removed.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-[#F3F4F6] text-sm font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteChat(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-medium transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Notification Toast */}
      {notification && (
        <div className="fixed top-6 right-6 z-[110] animate-in slide-in-from-top-2 duration-300">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl border ${
            notification.type === 'success' 
              ? 'bg-[#229ED9]/10 border-[#229ED9]/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-[#229ED9]" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-400" />
            )}
            <span className={`text-sm font-medium ${
              notification.type === 'success' ? 'text-[#229ED9]' : 'text-red-400'
            }`}>
              {notification.message}
            </span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 p-1 hover:bg-white/10 rounded-lg transition-all"
            >
              <X className={`w-4 h-4 ${
                notification.type === 'success' ? 'text-[#229ED9]' : 'text-red-400'
              }`} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatTelegramApp;
